import React, { useEffect, useState } from 'react';
import AgDataGrid from 'components/DataGrid/AgDataGrid';
import { useAppDispatch, useAppSelector } from 'helpers/redux';
import { filterParams } from '../../../../../helpers/agTable';
import { Link } from 'react-router-dom';
import { OvertimeModel } from '../../../../profile/models';
import DefaultModal from '../../../../../components/Modal/DefaultModal';
import OvertimeNotesDetail from '../../../../profile/absent/components/OvertimeNotesDetail';
import { UserCredential } from 'features/login/models';
import { RootState } from 'app/store';
import { customStatusOption, customWarnOption } from 'helpers/alertLabel';
import {
    changeOvertimeStatusByAdmin,
    reset,
} from 'features/approval/approval.reducer';
import { AsyncThunkAction, Dispatch } from '@reduxjs/toolkit';
import { OvertimeStatusAdmin } from 'features/approval/models';
import { ApiResponseWrapper, RequestStatus } from 'http-client';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { TbRefreshAlert } from 'react-icons/tb';
import { getOvertimeReport } from '../../attendanceReport.reducer';

const OvertimeReportTable = () => {
    const dispatch = useAppDispatch();
    const MySwal = withReactContent(Swal);
    const user = useAppSelector(
        (state: RootState) => state.user.userAuth ?? ({} as UserCredential)
    );
    const overtimeReportList = useAppSelector(
        (state) => state.attendanceReport.overtimeReportList ?? []
    );
    const overtimeRevertStatus = useAppSelector(
        (state: RootState) =>
            state.approval.overtimeRevertStatus ?? RequestStatus.pending
    );
    const error = useAppSelector((state: RootState) => state.approval.error);

    const [showNotesModal, setShowNotesModal] = useState<boolean>(false);
    const [singleOvertime, setSingleOvertime] = useState<OvertimeModel>(
        {} as OvertimeModel
    );

    useEffect(() => {
        if (
            overtimeRevertStatus !== undefined &&
            overtimeRevertStatus !== RequestStatus.pending
        ) {
            MySwal.fire(
                customStatusOption(
                    overtimeRevertStatus === RequestStatus.success,
                    error?.data?.message,
                    'status has been changed!',
                    'failed to change status'
                )
            ).then(() => {
                dispatch(reset());
                dispatch(getOvertimeReport({}));
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overtimeRevertStatus, error]);

    const NotesLinkCellRender = (props: any) => {
        // hide if last table row
        return (
            <>
                {props.data === undefined ? (
                    <></>
                ) : (
                    <Link
                        to={`#`}
                        className="underline text-blue-700"
                        onClick={() => onDetailClicked(props.data)}
                    >
                        See Notes
                    </Link>
                )}
            </>
        );
    };

    const onRevertClicked = (id: string) => {
        MySwal.fire(
            customWarnOption(
                'Revert Overtime',
                'Are you sure you want to revert this overtime request? This action cannot be undone.'
            )
        ).then((result) => {
            if (result.isConfirmed) {
                dispatch(
                    changeOvertimeStatusByAdmin({
                        id: id,
                        status: 0, // Assuming 0 is the status for "Requested"
                    })
                );
            }
        });
    };

    const ActionCellRender = (props: any) => {
        if (props.data === undefined) {
            return null;
        }

        return (
            <div className="flex items-center h-[100%]">
                {user.roleName.some(
                    (item) =>
                        item === 'Administrator' ||
                        item === 'Superadmin' ||
                        item === 'HRD'
                ) &&
                    props.data?.status !== 'Requested' && (
                        <button onClick={() => onRevertClicked(props.data?.id)}>
                            <TbRefreshAlert className="mx-1 text-2xl text-primary-color" />
                        </button>
                    )}
            </div>
        );
    };

    // Each Column Definition results in one Column.
    const [columnDefs, _setColumnDefs] = useState([
        { field: 'nip', headerName: 'NIP', filter: true },
        { field: 'team', headerName: 'Team', filter: true },
        { field: 'name', headerName: 'Name', filter: true },
        {
            field: 'ts_created',
            headerName: 'Created Date',
            filter: 'agDateColumnFilter',
            filterParams: filterParams,
            cellDataType: 'date',
        },
        { field: 'roleName', headerName: 'Role', filter: true },
        {
            field: 'reqMinute',
            headerName: 'Req Minutes',
            filter: true,
            aggFunc: 'sum',
        },
        {
            field: 'notes',
            headerName: 'Notes',
            cellRenderer: NotesLinkCellRender,
            cellStyle: () => ({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }),
            filter: false,
            sortable: false,
        },
        { field: 'status', headerName: 'Status', filter: true },
        { field: 'rejectionNote', headerName: 'Rejection Notes' },
        { field: 'approveMinute', headerName: 'Approved', aggFunc: 'sum' },
        {
            field: 'totalApproveMinute',
            headerName: 'Total Approved',
            aggFunc: 'sum',
        },
        { field: 'multiplier', headerName: 'Multiple' },
        { field: 'managerApproveByName', headerName: 'Manager Approved' },
        { field: 'approveByName', headerName: 'Approved By' },
        {
            headerName: 'Actions',
            field: 'actions',
            pinned: 'right',
            suppressCellFlash: true,
            minWidth: 220,
            cellRenderer: ActionCellRender,
            cellStyle: () => ({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }),
        },
    ]);

    const onDetailClicked = (overtime: OvertimeModel) => {
        setSingleOvertime(overtime);
        setShowNotesModal(true);
    };

    const toggleNotesModal = () => {
        setShowNotesModal((prevState) => !prevState);
        setSingleOvertime({} as OvertimeModel);
    };

    return (
        <div>
            <div className="mt-3">
                <div className="main-data-grid fielding mt-5">
                    <AgDataGrid
                        rowData={overtimeReportList}
                        columnDefs={columnDefs}
                        groupIncludeFooter={true}
                        groupIncludeTotalFooter={true}
                        supportSaveTableState
                        tableStateName={'report-overtime'}
                        defaultPageSize={20}
                    />
                    <DefaultModal
                        title={'Notes'}
                        show={showNotesModal}
                        onClose={toggleNotesModal}
                        body={<OvertimeNotesDetail data={singleOvertime} />}
                    />
                </div>
            </div>
        </div>
    );
};

export default OvertimeReportTable;
